import type { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { apiClient } from '@src/services/http-common'
import { EDisbursementTarget, type EPaymentMethod } from '@src/types/constants'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

const SCOPE = 'bankJobs'

export const bankJobQueryKeys = {
  all: [SCOPE] as const,
  list: (params: BankJobsParams) => [...bankJobQueryKeys.all, 'list', params] as const,
  years: (financingCompanyId: EFinancingCompany) => [...bankJobQueryKeys.all, 'years', financingCompanyId] as const,
  one: (financingCompanyId: EFinancingCompany, bankJobId: number) =>
    [...bankJobQueryKeys.all, 'file', financingCompanyId, bankJobId] as const,
  lenderPayments: (financingCompanyId: EFinancingCompany, bankJobId: number) =>
    [...bankJobQueryKeys.all, 'payments', financingCompanyId, bankJobId] as const,
}

// One row of GET {financingCompanyId}/disbursements/bank-jobs: a deposit (ACP file, cheque or direct
// deposit) that paid lender payments out.
// The company is a path segment, not a query parameter: CompanyAccessMiddleware checks it off the route.
export interface BankJobDto {
  bankJobId: number
  /** How the money moved: PreAuthorizedDebit for an ACP file, Cheque, or APTPay for a direct deposit. */
  bankOperationMethod: EPaymentMethod
  /**
   * The number the Monolith key carries on an imported deposit (ACP file id, cheque or deposit number);
   * the cheque number, else the job id, on an LMS-native one.
   */
  fileNumber: number
  /** The deposit date — the Monolith DepotD on an imported file. DateOnly as string from API. */
  depositOn: string
  createdByFullName: string
  /** What the file's lender payments add up to. */
  amount: number
  /** True when every lender payment of the file failed, cancelled or sent back by the bank: the red flag. */
  isFullyFailed: boolean
  /** True when some, but not all, failed: the yellow flag. */
  isPartiallyFailed: boolean
}

export interface BankJobsParams {
  financingCompanyId: EFinancingCompany
  /** Inclusive, on the deposit date. */
  from: string
  /** Inclusive; null keeps every file deposited from `from` on, future ones included. */
  to: string | null
}

const getBankJobs = async ({ financingCompanyId, from, to }: BankJobsParams) => {
  const params = new URLSearchParams({ from })
  if (to) params.set('to', to)
  const response = await apiClient.get<BankJobDto[]>(
    `${financingCompanyId}/disbursements/bank-jobs?${params.toString()}`,
  )
  return response.data
}

export function useGetBankJobs(params: BankJobsParams): UseQueryResult<BankJobDto[]> {
  return useQuery({
    queryKey: bankJobQueryKeys.list(params),
    queryFn: () => getBankJobs(params),
  })
}

const getBankJobYears = async (financingCompanyId: EFinancingCompany) => {
  const response = await apiClient.get<number[]>(`${financingCompanyId}/disbursements/bank-jobs/years`)
  return response.data
}

export function useGetBankJobYears(financingCompanyId: EFinancingCompany): UseQueryResult<number[]> {
  return useQuery({
    queryKey: bankJobQueryKeys.years(financingCompanyId),
    queryFn: () => getBankJobYears(financingCompanyId),
  })
}

const getBankJob = async (financingCompanyId: EFinancingCompany, bankJobId: number) => {
  const response = await apiClient.get<BankJobDto>(`${financingCompanyId}/disbursements/bank-jobs/${bankJobId}`)
  return response.data
}

/** One file, summarised like its row of the history — the header of its detail page. */
export function useGetBankJob(financingCompanyId: EFinancingCompany, bankJobId: number): UseQueryResult<BankJobDto> {
  return useQuery({
    queryKey: bankJobQueryKeys.one(financingCompanyId, bankJobId),
    queryFn: () => getBankJob(financingCompanyId, bankJobId),
  })
}

// One row of GET {financingCompanyId}/disbursements/bank-jobs/{bankJobId}/lender-payments: a lender
// payment the file carried. The history groups them by payee into offices.
export interface BankJobLenderPaymentDto {
  operationId: number
  loanId: number
  financingProgramId: EFinancingProgram
  borrowerFullName: string
  /** True when the payment pays out an account-to-account closing payment rather than disbursing the loan. */
  isAccountToAccountPayout: boolean
  /** True when the payment ended Failed: cancelled, or sent back by the bank. */
  isFailed: boolean
  /** True when the payment was cancelled — Failed with no bank failure code. */
  isCancelled: boolean
  /** The bank's failure code on a payment it sent back (EBankFailureCode); -1 on a cancelled one; null otherwise. */
  bankFailureCode: number | null
  /** Who cancelled the payment. Null unless it was cancelled. */
  cancelledByFullName: string | null
  /** The day it was cancelled. DateOnly as string from API; null unless cancelled, or when unknown. */
  cancelledOn: string | null
  /** The Monolith debourse's own description, shown verbatim. Null on an LMS-native payment. */
  description: string | null
  amount: number
  payeeId: string
  payeeType: EDisbursementTarget
  /** The merchant's name. Null for a borrower payee, or when it could not be resolved. */
  payeeName: string | null
  /**
   * The bank job line the payment was paid through: the bank's record of one office payment or ACP
   * line, which the detail page shows one card per. Null when the payment has no line yet.
   */
  bankJobLineId: number | null
  /** The line's amount, what the bank record carries (an account-to-account deducted). Null with bankJobLineId. */
  bankJobLineAmount: number | null
}

const getBankJobLenderPayments = async (financingCompanyId: EFinancingCompany, bankJobId: number) => {
  const response = await apiClient.get<BankJobLenderPaymentDto[]>(
    `${financingCompanyId}/disbursements/bank-jobs/${bankJobId}/lender-payments`,
  )
  return response.data
}

/** A card of the file's detail page: one bank job line, the bank's record of one office payment. */
export interface BankJobLine {
  /** The line, or the payee for a payment that has no line yet. */
  key: string
  payeeId: string
  /** The payee's name, or its id when the Merchants API could not name it. */
  name: string
  /** True for a merchant the Merchants API named; an unnamed one, like the `monolith*-1` sentinel, may have no manage-merchants page. */
  isKnownMerchant: boolean
  /** The line's amount, the bank record; a payment without a line sums with its payee's. */
  amount: number
  payments: BankJobLenderPaymentDto[]
}

// The payments arrive ordered by line then customer. Each line becomes one card, listed by payee
// name, with its failed payments first. A payment without a line groups with its payee's.
const groupByLine = (payments: BankJobLenderPaymentDto[]): BankJobLine[] => {
  const lines = new Map<string, BankJobLine>()
  for (const payment of payments) {
    const key = payment.bankJobLineId === null ? `payee:${payment.payeeId}` : `line:${payment.bankJobLineId}`
    const line = lines.get(key) ?? {
      key,
      payeeId: payment.payeeId,
      name: payment.payeeName ?? payment.payeeId,
      isKnownMerchant: payment.payeeType === EDisbursementTarget.Merchant && payment.payeeName !== null,
      amount: payment.bankJobLineAmount ?? 0,
      payments: [],
    }
    if (payment.bankJobLineAmount === null) line.amount += payment.amount
    line.payments.push(payment)
    lines.set(key, line)
  }
  // The sort is stable: on either side, the customers keep their order.
  for (const line of lines.values()) line.payments.sort((a, b) => Number(b.isFailed) - Number(a.isFailed))
  return [...lines.values()].sort((a, b) => a.name.localeCompare(b.name) || a.key.localeCompare(b.key))
}

export function useGetBankJobLines(
  financingCompanyId: EFinancingCompany,
  bankJobId: number,
): UseQueryResult<BankJobLine[]> {
  return useQuery({
    queryKey: bankJobQueryKeys.lenderPayments(financingCompanyId, bankJobId),
    queryFn: () => getBankJobLenderPayments(financingCompanyId, bankJobId),
    select: groupByLine,
  })
}
